import { useState, useEffect } from 'react'
import { FolderOpen, Sparkles, Clock } from 'lucide-react'

const API_BASE = import.meta.env.VITE_API_URL || ''

// Zenna's birthday
const ZENNA_BIRTHDAY = new Date('2026-02-07T00:00:00Z')

export default function ZennaStatsBar() {
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalProjects: 0,
    totalGenerations: 0
  })
  const [age, setAge] = useState('')

  useEffect(() => {
    fetchStats()
    updateAge()
    
    const statsInterval = setInterval(fetchStats, 30000)
    const ageInterval = setInterval(updateAge, 60000)
    
    return () => {
      clearInterval(statsInterval)
      clearInterval(ageInterval)
    }
  }, [])

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_BASE}/stats`)
      if (!res.ok) return
      
      const data = await res.json()
      
      setStats({
        totalUsers: data.total_users || 0,
        activeUsers: data.active_users_now || 0,
        totalProjects: data.total_projects || 0,
        totalGenerations: data.total_generations || 0
      })
    } catch (err) {
      console.error('Failed to fetch stats:', err)
    }
  }

  const updateAge = () => {
    const now = new Date()
    const diff = now - ZENNA_BIRTHDAY
    
    if (diff < 0) {
      setAge('Coming Soon!')
      return
    }
    
    const totalHours = Math.floor(diff / (1000 * 60 * 60))
    const days = Math.floor(totalHours / 24)
    const months = Math.floor(days / 30)
    const years = Math.floor(days / 365)
    
    if (years > 0) {
      const remainingMonths = Math.floor((days % 365) / 30)
      if (remainingMonths > 0) {
        setAge(`${years} year${years > 1 ? 's' : ''}, ${remainingMonths} month${remainingMonths > 1 ? 's' : ''} old`)
      } else {
        setAge(`${years} year${years > 1 ? 's' : ''} old`)
      }
    } else if (months > 0) {
      const remainingDays = days % 30
      if (remainingDays > 0) {
        setAge(`${months} month${months > 1 ? 's' : ''}, ${remainingDays} day${remainingDays > 1 ? 's' : ''} old`)
      } else {
        setAge(`${months} month${months > 1 ? 's' : ''} old`)
      }
    } else if (days >= 1) {
      setAge(`${days} day${days > 1 ? 's' : ''} old`)
    } else if (totalHours >= 1) {
      setAge(`${totalHours} hour${totalHours > 1 ? 's' : ''} old 🎉`)
    } else {
      setAge('Just born! 🎉')
    }
  }

  return (
    <div className="bg-gradient-to-r from-purple-900/80 via-indigo-900/80 to-purple-900/80 border-b border-purple-500/20 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-4 py-2">
        <div className="flex flex-wrap items-center justify-center gap-4 md:gap-8 text-sm">
          {/* Zenna's Age */}
          <div className="flex items-center gap-2 text-purple-200">
            <Clock className="w-4 h-4 text-purple-400" />
            <span className="text-purple-400">Zenna:</span>
            <span className="font-medium text-white">{age}</span>
          </div>
          
          <div className="hidden md:block w-px h-4 bg-purple-500/30" />
                   
          {/* Active Now */}
          <div className="flex items-center gap-2 text-purple-200">
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
            <span className="font-bold text-green-400">{stats.activeUsers}</span>
            <span className="text-purple-300">online</span>
          </div>
          
          <div className="hidden md:block w-px h-4 bg-purple-500/30" />
          
          {/* Total Projects */}
          <div className="flex items-center gap-2 text-purple-200">
            <FolderOpen className="w-4 h-4 text-amber-400" />
            <span className="font-bold text-white">{stats.totalProjects.toLocaleString()}</span>
            <span className="text-purple-300">projects</span>
          </div>
          
          {/* Total Generations */}
          <div className="flex items-center gap-2 text-purple-200">
            <Sparkles className="w-4 h-4 text-pink-400" />
            <span className="font-bold text-white">{stats.totalGenerations.toLocaleString()}</span>
            <span className="text-purple-300">designs</span>
          </div>
        </div>
      </div>
    </div>
  )
}